import React from "react";
import {
  SearchBox,
  Dropdown,
  PrimaryButton,
  TextField,
  Checkbox,
  FontSizes,
  fontFace,
} from "office-ui-fabric-react";
import { FormRow, FormLabel } from "./BaseComponents.jsx";
import Theme from "../../styles/theme.js";
import "styles/custom.css";
import theme from "../../styles/theme.js";

// Standard input styles
const inputStyles = {
  root: { width: Theme.layout.inputWidth },
  color: Theme.colors.text,
};

const labelTopStyle = {
  display: "block",
  marginBottom: Theme.spacing.xs,
  color: Theme.colors.text,
  fontSize: Theme.typography.fontSize.small,
  fontWeight: Theme.typography.fontWeight.medium,
  fontFamily: Theme.typography.fontFamily,
};

// Standardized SearchBox with label above
export const StandardSearchBox = ({ label, labelStyle, ...props }) => (
  <FormRow>
    <FormLabel style={{ ...labelTopStyle, ...labelStyle }}>{label}</FormLabel>
    <SearchBox styles={inputStyles} {...props} />
  </FormRow>
);

// Standardized Dropdown with label above
export const StandardDropdown = ({ label, labelStyle, ...props }) => (
  <FormRow>
    <FormLabel style={{ ...labelTopStyle, ...labelStyle }}>{label}</FormLabel>
    <Dropdown styles={inputStyles} {...props} />
  </FormRow>
);

// Standardized TextField with label above
export const StandardTextField = ({ label, labelStyle, ...props }) => (
  <FormRow>
    <FormLabel style={{ ...labelTopStyle, ...labelStyle }}>{label}</FormLabel>
    <TextField styles={inputStyles} color={Theme.colors.text} {...props} />
  </FormRow>
);

// Standardized Checkbox with label above
export const StandardCheckbox = ({ label, labelStyle, ...props }) => (
  <FormRow>
    <FormLabel style={{ ...labelTopStyle, ...labelStyle }}>{label}</FormLabel>
    <Checkbox color={Theme.colors.text} {...props} />
  </FormRow>
);

// Standardized buttons with consistent sizing
export const StandardButton = ({
  size = "normal",
  destructive = false,
  ...props
}) => {
  const getButtonWidth = () => {
    switch (size) {
      case "wide":
        return Theme.layout.wideButtonWidth;
      case "extra-wide":
        return Theme.layout.buttonExtraWideWidth;
      default:
        return Theme.layout.buttonWidth;
    }
  };

  const buttonStyles = {
    height: Theme.layout.buttonHeight,
    width: getButtonWidth(),
    ...(destructive && {
      root: {
        backgroundColor: Theme.colors.error,
        borderColor: Theme.colors.error,
      },
      rootHovered: { backgroundColor: "#b71c1c", borderColor: "#b71c1c" },
      rootPressed: { backgroundColor: "#9a0007", borderColor: "#9a0007" },
    }),
  };

  return (
    <FormRow>
      <PrimaryButton styles={buttonStyles} {...props} />
    </FormRow>
  );
};

// Status/error message component
export const StatusMessage = ({
  children,
  type = "info", // 'info', 'error', 'success', 'warning'
  style = {},
}) => {
  const getClassName = () => {
    switch (type) {
      case "error":
        return "error-message";
      case "success":
        return "success-message";
      case "warning":
        return "warning-message";
      default:
        return "";
    }
  };

  return (
    <div
      className={getClassName()}
      style={{
        margin: Theme.layout.contentPadding,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// Loading indicator
export const LoadingMessage = ({ message = "Loading..." }) => (
  <StatusMessage>{message}</StatusMessage>
);

export const StandardText = ({
  variant = "body", // 'body', 'small', 'large'
  color,
  style,
  children,
  ...props
}) => {
  const getTextStyle = () => {
    const baseStyle = {
      fontFamily: Theme.typography.fontFamily,
      color: color || Theme.colors.text,
      margin: 0,
      padding: 0,
    };

    switch (variant) {
      case "small":
        return {
          ...baseStyle,
          fontSize: Theme.typography.fontSize.small,
          fontWeight: Theme.typography.fontWeight.normal,
        };
      case "large":
        return {
          ...baseStyle,
          fontSize: Theme.typography.fontSize.large,
          fontWeight: Theme.typography.fontWeight.medium,
        };
      default: // 'body'
        return {
          ...baseStyle,
          fontSize: Theme.typography.fontSize.medium,
          fontWeight: Theme.typography.fontWeight.normal,
        };
    }
  };

  return (
    <p
      style={{
        ...getTextStyle(),
        ...style,
      }}
      {...props}
    >
      {children}
    </p>
  );
};

export const TextFieldWithTooltip = ({
  tooltipContent,
  label,
  labelStyle,
  ...textFieldProps
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <FormRow>
      <FormLabel style={{ ...labelTopStyle, ...labelStyle }}>{label}</FormLabel>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: Theme.layout.inputWidth,
        }}
      >
        <TextField
          styles={inputStyles}
          color={Theme.colors.text}
          {...textFieldProps}
        />
        <div
          style={{
            cursor: "pointer",
            fontSize: "16px",
            color: Theme.colors.primary,
            fontWeight: "bold",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            border: `2px solid ${Theme.colors.primary}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            flexShrink: 0,
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          ?
        </div>

        {showTooltip && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "0",
              zIndex: 1000,
              backgroundColor: "white",
              border: `1px solid ${Theme.colors.border}`,
              borderRadius: "4px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              maxWidth: "300px",
              marginTop: "4px",
            }}
          >
            {tooltipContent}
          </div>
        )}
      </div>
    </FormRow>
  );
};

export const ButtonWithTooltip = ({
  tooltipContent,
  size = "normal",
  ...buttonProps
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const getButtonWidth = () => {
    switch (size) {
      case "wide":
        return Theme.layout.wideButtonWidth;
      case "extra-wide":
        return Theme.layout.buttonExtraWideWidth;
      default:
        return Theme.layout.buttonWidth;
    }
  };

  const buttonStyles = {
    height: Theme.layout.buttonHeight,
    width: getButtonWidth(),
  };

  return (
    <FormRow>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <PrimaryButton styles={buttonStyles} {...buttonProps} />
        <div
          style={{
            cursor: "pointer",
            fontSize: "16px",
            color: Theme.colors.primary,
            fontWeight: "bold",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            border: `2px solid ${Theme.colors.primary}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            flexShrink: 0,
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          ?
        </div>

        {showTooltip && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "0",
              zIndex: 1000,
              backgroundColor: "white",
              border: `1px solid ${Theme.colors.border}`,
              borderRadius: "4px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              maxWidth: "300px",
              marginTop: "4px",
            }}
          >
            {tooltipContent}
          </div>
        )}
      </div>
    </FormRow>
  );
};

export const DropdownWithTooltip = ({
  tooltipContent,
  label,
  labelStyle,
  ...textFieldProps
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  return (
    <FormRow>
      <FormLabel style={{ ...labelTopStyle, ...labelStyle }}>{label}</FormLabel>
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: Theme.layout.inputWidth,
        }}
      >
        <Dropdown
          styles={inputStyles}
          color={Theme.colors.text}
          {...textFieldProps}
        />
        <div
          style={{
            cursor: "pointer",
            fontSize: "16px",
            color: Theme.colors.primary,
            fontWeight: "bold",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            border: `2px solid ${Theme.colors.primary}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            flexShrink: 0,
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          ?
        </div>

        {showTooltip && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "0",
              zIndex: 1000,
              backgroundColor: "white",
              border: `1px solid ${Theme.colors.border}`,
              borderRadius: "4px",
              padding: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              maxWidth: "300px",
              marginTop: "4px",
            }}
          >
            {tooltipContent}
          </div>
        )}
      </div>
    </FormRow>
  );
};

export const TextAutoComplete = ({
  label,
  labelStyle,
  description,
  value = "",
  onChange,
  onSelect,
  options = [],
  placeholder,
  disabled,
  maxSuggestions = 10,
}) => {
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);

  const query = (value || "").trim().toLowerCase();

  const filtered = React.useMemo(() => {
    if (!query) return [];
    return options
      .filter((name) => name.toLowerCase().includes(query))
      .slice(0, maxSuggestions);
  }, [options, query, maxSuggestions]);

  const showList = open && !disabled && filtered.length > 0;

  const selectValue = (name) => {
    onSelect?.(name);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e) => {
    if (!showList) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      selectValue(filtered[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <FormRow>
      <FormLabel style={{ ...labelTopStyle, ...labelStyle }}>{label}</FormLabel>
      <div style={{ position: "relative", width: Theme.layout.inputWidth }}>
        <TextField
          styles={inputStyles}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onChange={(_, newValue) => {
            onChange?.(newValue || "");
            setOpen(true);
            setActiveIndex(-1);
          }}
          onKeyDown={onKeyDown}
        />

        {showList && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1000,
              background: Theme.colors.cardBackground,
              border: `1px solid ${Theme.colors.border}`,
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {filtered.map((name, index) => (
              <div
                key={name}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectValue(name)}
                onMouseEnter={() => setActiveIndex(index)}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  background: index === activeIndex ? "#f3f2f1" : "#fff",
                }}
              >
                {name}
              </div>
            ))}
          </div>
        )}
      </div>

      {description && (
        <div
          style={{
            marginTop: Theme.spacing.xs,
            color: "#666",
            fontSize: Theme.typography.fontSize.small,
          }}
        >
          {description}
        </div>
      )}
    </FormRow>
  );
};
